/**
 * Company Profile Wizard Component
 * 
 * 4-step wizard to set up company legal profile:
 * Step 1: Upload RC or Manual Entry
 * Step 2: Review Extracted Data
 * Step 3: Complete Profile (Tax Regime, Activity Type, etc.)
 * Step 4: Review Obligations Summary
 */

import { useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SplitScreenRCUpload } from './SplitScreenRCUpload';
import { RCReviewDialog } from './RCReviewDialog';
import { CompanyProfileForm } from './CompanyProfileForm';
import { ObligationsSummary } from './ObligationsSummary';
import type { RCExtractedData, CompanyLegalProfile } from '@lebusiness/types';

interface WizardStep {
  id: number;
  title: string;
  description: string;
}

const STEPS: WizardStep[] = [
  {
    id: 1,
    title: 'Upload RC',
    description: 'Téléchargez votre Registre de Commerce',
  },
  {
    id: 2,
    title: 'Review Data',
    description: 'Vérifiez les données extraites',
  },
  {
    id: 3,
    title: 'Complete Profile',
    description: 'Complétez votre profil juridique',
  },
  {
    id: 4,
    title: 'Review Obligations',
    description: 'Vérifiez vos obligations fiscales',
  },
];

interface CompanyProfileWizardProps {
  onComplete: (profile: CompanyLegalProfile) => void;
  onCancel?: () => void;
}

export function CompanyProfileWizard({ onComplete, onCancel }: CompanyProfileWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [extractedData, setExtractedData] = useState<RCExtractedData | null>(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [confirmedData, setConfirmedData] = useState<RCExtractedData | null>(null);
  const [profileData, setProfileData] = useState<CompanyLegalProfile | null>(null);

  // Step 1: Handle RC upload and extraction
  const handleExtractComplete = (data: RCExtractedData) => {
    setExtractedData(data);
    setShowReviewDialog(true);
  };

  const handleExtractError = (error: string) => {
    console.error('OCR Error:', error);
  };

  // Step 2: Handle review confirmation
  const handleReviewConfirm = (data: RCExtractedData) => {
    setConfirmedData(data);
    setShowReviewDialog(false);
    setCurrentStep(3);
  };

  const handleReviewCancel = () => {
    setShowReviewDialog(false);
    setExtractedData(null);
  };

  // Step 3: Handle profile form completion
  const handleProfileComplete = (profile: CompanyLegalProfile) => {
    setProfileData(profile);
    setCurrentStep(4);
  };

  // Step 4: Handle final confirmation
  const handleFinalConfirm = () => {
    if (profileData) {
      onComplete(profileData);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkipUpload = () => {
    setCurrentStep(3);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => (
          <div key={step.id} className="flex flex-1 items-center">
            <div className="flex flex-col items-center">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all ${
                  currentStep === step.id
                    ? 'border-primary bg-primary text-white'
                    : currentStep > step.id
                    ? 'border-green-500 bg-green-500 text-white'
                    : 'border-gray-300 bg-white text-gray-400 dark:border-gray-600 dark:bg-gray-800'
                }`}
              >
                {currentStep > step.id ? (
                  <CheckCircle className="h-6 w-6" />
                ) : (
                  <span className="text-sm font-semibold">{step.id}</span>
                )}
              </div>
              <div className="mt-2 text-center">
                <p
                  className={`text-xs font-medium ${
                    currentStep >= step.id ? 'text-gray-900 dark:text-white' : 'text-gray-500'
                  }`}
                >
                  {step.title}
                </p>
                <p className="text-xs text-gray-500">{step.description}</p>
              </div>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={`mx-2 h-0.5 flex-1 transition-all ${
                  currentStep > step.id ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            <div>
              <CardTitle>{STEPS[currentStep - 1].title}</CardTitle>
              <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Step 1: Upload RC */}
          {currentStep === 1 && (
            <SplitScreenRCUpload
              onComplete={handleExtractComplete}
              onError={handleExtractError}
            />
          )}

          {/* Step 2: Review happens in dialog */}
          {currentStep === 2 && (
            <div className="py-8 text-center">
              <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
              <h3 className="mt-4 text-lg font-semibold">Données confirmées</h3>
              <p className="mt-2 text-sm text-gray-600">
                Passez à l'étape suivante pour compléter votre profil
              </p>
            </div>
          )}

          {/* Step 3: Complete Profile */}
          {currentStep === 3 && (
            <CompanyProfileForm
              initialData={confirmedData}
              onComplete={handleProfileComplete}
              onBack={handleBack}
            />
          )}

          {/* Step 4: Review Obligations */}
          {currentStep === 4 && profileData && (
            <ObligationsSummary
              profile={profileData}
              onConfirm={handleFinalConfirm}
              onBack={handleBack}
            />
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      {extractedData && (
        <RCReviewDialog
          open={showReviewDialog}
          onOpenChange={setShowReviewDialog}
          extractedData={extractedData}
          onConfirm={handleReviewConfirm}
          onCancel={handleReviewCancel}
        />
      )}

      {/* Navigation Buttons */}
      {currentStep !== 1 && currentStep !== 3 && currentStep !== 4 && (
        <div className="flex justify-between">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Annuler
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
